import { formatAddress } from "@inrev/common";
import { SuretyAccountPreview } from "@portal/api/account/agent";
import { SuretyBondPreview } from "@portal/api/bond/agent";
import { BondRequestPreview } from "@portal/api/bond-request/agent";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import type { SearchResultClickedHandler } from "@portal/hooks/useSearchAnalytics";
import { useGlobalSearchDispatch } from "@portal/providers/GlobalSearchProvider";
import { useWindowSize } from "@uidotdev/usehooks";
import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { match } from "ts-pattern";
import type { SearchResultSource } from "../../../analytics";
import {
	formatSuretyAccountDescription,
	formatSuretyBondDescription,
	formatSuretyQuoteDescription,
} from "../../../utils/string";
import { Icon, IconType } from "../../primitives";
import {
	BondStatusTagType,
	getStatusTagByBondStatus,
	getStatusTagByStatus,
	StatusTagType,
} from "../StatusTag";
import { AccountSearchEntity, BondRequestSearchEntity, BondSearchEntity } from "./types";

export type GlobalSearchResultProps = (
	| AccountSearchEntity
	| BondRequestSearchEntity
	| BondSearchEntity
) & {
	resultSource: SearchResultSource;
	onResultClicked: SearchResultClickedHandler;
	/** Invoked on result click before navigation. Defaults to closing the global-search modal. */
	onNavigate?: () => void;
};

// Keep props as a union to leverage exhaustiveness checking with ts-pattern
export const GlobalSearchResult: FC<GlobalSearchResultProps> = (props) => {
	const { width } = useWindowSize();
	const navigate = useNavigate();
	const { closeGlobalSearch } = useGlobalSearchDispatch();
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const handleNavigate = props.onNavigate ?? closeGlobalSearch;

	const { url, iconType, title, subtitle, status, hoverTooltip } = match(props)
		.with({ relationType: "account" }, () => {
			const account = props.entity as SuretyAccountPreview;
			const primaryCompany = account.companies.find(
				(company) => company.id === account.primaryCompanyId,
			);
			return {
				url: `/accounts/${account.id}`,
				iconType: "building-office-alt",
				title: formatSuretyAccountDescription(account),
				subtitle: primaryCompany && formatAddress(primaryCompany.address),
				status: account.status,
				hoverTooltip: undefined,
			};
		})
		.with({ relationType: "bondRequest" }, () => {
			const bondRequest = props.entity as BondRequestPreview;
			return {
				url: `/accounts/${bondRequest.accountId}/requests/${bondRequest.id}`,
				iconType: "clipboard",
				title: formatSuretyQuoteDescription(bondRequest, true),
				subtitle: bondRequest.accountName,
				status: bondRequest.status,
				hoverTooltip: isMinimalUIEnabled
					? bondRequest.name
						? `Project Name: ${bondRequest.name}`
						: undefined
					: bondRequest.description
						? `Description: ${bondRequest.description}`
						: undefined,
			};
		})
		.with({ relationType: "bond" }, () => {
			const bond = props.entity as SuretyBondPreview;
			return {
				url: `/bonds/${bond.id}`,
				iconType: "shield-check",
				title: formatSuretyBondDescription(bond, true),
				subtitle: `${bond.number} | Obligee: ${bond.obligeeName}`,
				status: bond.status,
				hoverTooltip: isMinimalUIEnabled
					? bond.name
						? `Project Name: ${bond.name}`
						: undefined
					: `Description: ${bond.description}`,
			};
		})
		.exhaustive();

	return (
		<div
			data-testid="global-search-result"
			data-tooltip-id={url}
			data-tooltip-content={hoverTooltip}
			data-tooltip-delay-show={300}
			data-tooltip-offset={-27}
			data-tooltip-place="bottom"
			className="flex items-center space-x-3 w-full px-7 py-3 hover:bg-surface-neutral cursor-pointer border-b border-bc-subtle last:border-b-0 text-left"
			onClick={() => {
				props.onResultClicked(props.relationType, props.entity.id, props.resultSource);
				handleNavigate();
				navigate(url);
			}}
		>
			<div data-testid="global-search-result-icon" className="text-2xl">
				<Icon type={iconType as IconType} size="small" color="tertiary" />
			</div>
			<div className="flex flex-col flex-1 min-w-0">
				<span
					data-testid="global-search-result-title"
					className="text-sm font-medium text-primary truncate"
				>
					{title}
				</span>
				{subtitle && (
					<span
						data-testid="global-search-result-subtitle"
						className="text-xs text-tertiary truncate"
					>
						{subtitle}
					</span>
				)}
			</div>
			<div data-testid="global-search-result-status" className="flex flex-shrink-0 justify-end">
				{props.relationType === "bond"
					? getStatusTagByBondStatus(status as BondStatusTagType, {
							condensed: true,
							iconOnly: Boolean(width && width < 640),
						})
					: getStatusTagByStatus(status as StatusTagType, {
							condensed: true,
							iconOnly: Boolean(width && width < 640),
						})}
			</div>
			{hoverTooltip && (
				<ReactTooltip
					style={{
						padding: "4px 8px",
						maxWidth: 450,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
					noArrow
					id={url}
				/>
			)}
		</div>
	);
};
